<template>
    <div
        :class="{
            'apply-dropdown': true,
            'apply-dropdown--isChecked': isChecked,
            'apply-dropdown--isMobile': isDisplayMobile,
        }"
    >
        <div v-if="isChecked" class="apply-dropdown__cta">
            <VJoyButton icon="file-check" data-testid="apply-to-btn" variant="ghost" size="xsmall" @click="onToggleDropdown(day.dayNumber)">
                {{ t('activity-tracker.apply.to') }}
                <VJoyIcon size="xxsmall" :name="activeDropdown?.dayNumber === day.dayNumber ? 'chevron-up' : 'chevron-down'" />
            </VJoyButton>
        </div>

        <Transition name="fade-scale">
            <VJoyDropdownList
                v-if="activeDropdown?.dayNumber === day.dayNumber && !isDisplayMobile"
                data-testid="dropdown-list"
                class="apply-dropdown__list"
            >
                <template #dropdown-list-header>
                    <div class="apply-dropdown__list-header">{{ t('activity-tracker.apply.to') }}...</div>
                </template>
                <template #dropdown-list-items>
                    <VJoyCheckboxGroup
                        data-testid="week-days-checkbox"
                        :value="daysSelected"
                        :options="daysOfTheWeeks"
                        @update:value="(value: string[]) => onUpdateCheckedValue(value)"
                    />
                </template>

                <template #dropdown-list-footer>
                    <VJoyButton data-testid="apply-button" size="xsmall" :label="t('activity-tracker.apply.cta')" @click="applyHandler" />
                </template>
            </VJoyDropdownList>
        </Transition>

        <VJoyBottomSheet v-if="isDisplayMobile && activeDropdown?.dayNumber === day.dayNumber" open @bottom-sheet:close="closeDropdownHandler">
            <template #bottom-sheet-content>
                <div class="apply-mobile">
                    <div class="apply-mobile__header">{{ t('activity-tracker.apply.to') }}...</div>
                    <VJoyCheckboxGroup
                        :value="daysSelected"
                        :options="daysOfTheWeeks"
                        @update:value="(value: string[]) => onUpdateCheckedValue(value)"
                    ></VJoyCheckboxGroup>
                </div>
            </template>
            <template #bottom-sheet-cta>
                <VJoyButton variant="primary" :label="t('activity-tracker.apply.cta')" @click.prevent.self="applyHandler" />
            </template>
        </VJoyBottomSheet>
    </div>
</template>

<script setup lang="ts">
    import {ref, toRefs} from 'vue';
    import {useTranslation} from '#imports';
    import {useDisplayMobile} from '@malt/utils';
    import {VJoyBottomSheet, VJoyButton, VJoyDropdownList, VJoyIcon, VJoyCheckboxGroup} from '@maltjoy/core-vue';
    import type {Day, Days} from '@/common/types';

    const {isDisplayMobile} = useDisplayMobile();
    const {t} = useTranslation();

    type ComponentProps = {
        day: Day;
        weeksOfTheMonth: {
            daysOfWeek: Days;
            weekRangeText: string;
        }[];
        isChecked: boolean;
        activeDropdown: {dayNumber: number} | null;
    };

    const emit = defineEmits<{
        toggleDropdown: [state: number];
        closeDropdown: [daysSelected?: string[]];
    }>();

    const props = defineProps<ComponentProps>();

    const {day, isChecked, activeDropdown} = toRefs(props);

    const daysOfTheWeeks = ref<Array<{value: string; label: string; id: string}>>([
        {value: '0', label: `${t('day.1.plural')}`, id: '0'},
        {value: '1', label: `${t('day.2.plural')}`, id: '1'},
        {value: '2', label: `${t('day.3.plural')}`, id: '2'},
        {value: '3', label: `${t('day.4.plural')}`, id: '3'},
        {value: '4', label: `${t('day.5.plural')}`, id: '4'},
        {value: '5', label: `${t('day.6.plural')}`, id: '5'},
        {value: '6', label: `${t('day.7.plural')}`, id: '6'},
    ]);

    const daysSelected = ref<string[]>([]);

    function onToggleDropdown(dayNumber: number) {
        emit('toggleDropdown', dayNumber);
    }

    function closeDropdownHandler() {
        emit('closeDropdown');
    }

    function onUpdateCheckedValue(days: string[]) {
        daysSelected.value = days;
    }

    function applyHandler() {
        emit('closeDropdown', daysSelected.value);
    }
</script>

<style lang="scss" scoped>
    :deep(.joy-checkbox__wrapper) {
        :hover {
            transition: var(--joy-transition-duration-short);
            background-color: var(--joy-color-secondary-10);
        }
    }

    :deep(.joy-multi-checkbox) {
        gap: 0;
    }

    :deep(.joy-checkbox) {
        padding: var(--joy-core-spacing-4) 0px var(--joy-core-spacing-4) var(--joy-core-spacing-4);
    }

    .apply-dropdown {
        position: relative;
        cursor: pointer;

        &__list-header {
            font-weight: var(--joy-font-weight-bold);
            color: var(--joy-color-neutral-60);
        }

        :deep(.joy-dropdown-list__items) {
            padding: 0;
        }

        :deep(span .icon) {
            display: flex;
            align-items: center;
            font-size: var(--joy-font-size-primary-400);
        }

        &--isMobile {
            margin-top: var(--joy-core-spacing-7);
        }

        &--isChecked {
            display: flex;
            margin-left: auto;
            justify-content: flex-end;
        }

        &__cta {
            display: flex;
            color: var(--joy-color-secondary-30);
            gap: var(--joy-core-spacing-1);
            justify-content: flex-end;
            align-items: center;
            font-size: var(--joy-font-size-primary-300);

            :deep(span i.icon-chevron-down) {
                font-size: 8px;
            }
            :deep(span i.icon-chevron-up) {
                font-size: 8px;
            }
        }

        &__list {
            position: absolute;
            right: -10px;
            z-index: 2;
            margin-top: var(--joy-core-spacing-10);

            :deep(footer) {
                box-shadow: none;
                float: right;
            }
        }
    }

    .apply-mobile {
        &__header {
            color: var(--joy-color-neutral-60);
            font-size: var(--joy-font-size-primary-400);
            font-weight: var(--joy-font-weight-bold);
            padding: var(--joy-core-spacing-4) 0px var(--joy-core-spacing-4) var(--joy-core-spacing-4);
        }
    }
</style>
